/**
 * Personalization Editor Component
 * Carbon Design System editor for content personalization rules
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Button,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Toggle,
  Select,
  SelectItem,
  TextInput,
  Modal,
  Tile,
  InlineNotification,
} from '@carbon/react';
import { Add, Edit, TrashCan, View, Settings } from '@carbon/icons-react';

import type {
  PersonalizationRule,
  PersonalizationCondition,
  PersonalizationAction,
  VisitorContextPreview,
  ContentPreviewOptions,
} from '../../../shared/interfaces/ContentManagementAdmin';

interface PersonalizationEditorProps {
  contentId?: string;
  onRulesUpdate: (rules: PersonalizationRule[]) => void;
  onPreview: (options: ContentPreviewOptions) => void;
}

const PersonalizationEditor: React.FC<PersonalizationEditorProps> = ({ contentId, onRulesUpdate, onPreview }) => {
  const [rules, setRules] = useState<PersonalizationRule[]>([]);
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [previewContexts, setPreviewContexts] = useState<VisitorContextPreview[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (contentId) {
      loadPersonalizationRules();
    }
  }, [contentId]);

  const loadPersonalizationRules = async () => {
    // Implementation would load existing personalization rules
    setLoading(true);
    try {
      // Mock data for now
      setRules([
        {
          id: '1',
          name: 'Educator Welcome',
          description: 'Personalized content for educators',
          conditions: [],
          actions: [],
          priority: 1,
          active: true,
        },
      ]);
    } catch (error) {
      console.error('Failed to load personalization rules:', error);
    } finally {
      setLoading(false);
    }
  };

  const headers = [
    { key: 'name', header: 'Rule Name' },
    { key: 'description', header: 'Description' },
    { key: 'priority', header: 'Priority' },
    { key: 'active', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const rows = rules.map(rule => ({
    id: rule.id,
    name: rule.name,
    description: rule.description,
    priority: rule.priority,
    active: rule.active ? <Tag type="green">Active</Tag> : <Tag type="gray">Inactive</Tag>,
    actions: (
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button kind="ghost" renderIcon={Edit} size="sm">
          Edit
        </Button>
        <Button kind="ghost" renderIcon={View} size="sm">
          Preview
        </Button>
        <Button kind="ghost" renderIcon={TrashCan} size="sm">
          Delete
        </Button>
      </div>
    ),
  }));

  if (!contentId) {
    return (
      <Tile style={{ textAlign: 'center', padding: '2rem' }}>
        <p>Select content to configure personalization rules</p>
      </Tile>
    );
  }

  return (
    <div className="personalization-editor">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        <div>
          <h3>Personalization Rules</h3>
          <p style={{ color: 'var(--cds-text-02)' }}>Configure content variations based on visitor context</p>
        </div>

        <Button kind="primary" renderIcon={Add} onClick={() => setRuleModalOpen(true)}>
          Add Rule
        </Button>
      </div>

      <DataTable
        rows={rows}
        headers={headers}
        render={({ rows, headers, getHeaderProps, getRowProps, getTableProps }) => (
          <TableContainer>
            <Table {...getTableProps()}>
              <TableHead>
                <TableRow>
                  {headers.map(header => (
                    <TableHeader key={header.key} {...getHeaderProps({ header })}>
                      {header.header}
                    </TableHeader>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={headers.length} style={{ textAlign: 'center', padding: '2rem' }}>
                      <p style={{ color: 'var(--cds-text-02)' }}>No personalization rules configured</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map(row => (
                    <TableRow key={row.id} {...getRowProps({ row })}>
                      {row.cells.map(cell => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      />

      <Modal
        open={ruleModalOpen}
        onRequestClose={() => setRuleModalOpen(false)}
        modalHeading="Create Personalization Rule"
        size="lg"
      >
        <p>Personalization rule editor coming soon...</p>
      </Modal>
    </div>
  );
};

export default PersonalizationEditor;
